/**
 * Translate outside React, with a dependable English fallback.
 *
 * The import is `i18next` (the library singleton), NOT `src/i18n/i18n`: that
 * module calls `.init()` at import, so pulling it into a non-React module
 * initialises i18next as a side-effect of every test that reaches the file and
 * silently overrides a `resources: {}` setup. It is the same object at runtime
 * — the app root imports `src/i18n/i18n`, which configures this very instance.
 *
 * Until that has happened, `t()` would echo the key back, so the caller's
 * English string is returned instead. That is the whole point of passing a
 * `defaultValue`, and it is what a unit test that never mounts the app sees.
 */
import i18n from 'i18next';

/** Fill `{{name}}` placeholders the way i18next would. */
function interpolate(text: string, params: Readonly<Record<string, unknown>>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    // A missing OR explicitly-undefined param leaves the placeholder alone,
    // matching i18next rather than printing the string "undefined".
    if (value === undefined) {
      return match;
    }
    // S6551: only values with a meaningful string form are interpolated — an
    // object would render "[object Object]", so its placeholder stays put.
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return value.toString();
    }
    return match;
  });
}

/**
 * Translate `key`, falling back to `fallback`. `params` are interpolated on
 * both paths, so a message reads the same whichever branch produced it.
 */
export function i18nMessage(
  key: string,
  fallback: string,
  params?: Readonly<Record<string, unknown>>,
): string {
  if (!i18n.isInitialized) {
    return params === undefined ? fallback : interpolate(fallback, params);
  }
  return i18n.t(key, { defaultValue: fallback, ...params });
}
